// src/utils/apiClient.js
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Centralized API client that automatically handles:
 * - Authorization header
 * - 401 (Token expired) → SweetAlert warning + redirect to login
 * - Common error handling
 */
class ApiClient {
    constructor() {
        this.baseURL = API_BASE_URL;
        this.isHandling401 = false; // ✅ Prevent multiple alerts
    }

    getToken() {
        return localStorage.getItem("neuro_patientportal_auth_token");
    }

    handleUnauthorized() {
        // ✅ Prevent multiple SweetAlerts if many API calls fail at once
        if (this.isHandling401) return;
        this.isHandling401 = true;

        console.warn("⚠️ [401] Session expired. Preparing redirect...");

        // ✅ Clear all auth data immediately
        localStorage.removeItem("neuro_patientportal_auth_token");
        localStorage.removeItem("neuro_patientportal_userName");

        // ✅ Show SweetAlert immediately (no button, auto-close in 3s)
        Swal.fire({
            icon: "warning",
            title: "Session Expired",
            text: "Your session has expired. Please log in again.",
            showConfirmButton: false,       // ❌ Removed Login button
            allowOutsideClick: false,
            allowEscapeKey: false,
            timer: 3000,                    // ⏰ Auto-close after 3 seconds
            timerProgressBar: true,         // Progress bar animation
            didOpen: () => {
                // Ensure it appears above modals/overlays
                const swalEl = document.querySelector(".swal2-container");
                if (swalEl) {
                    swalEl.style.zIndex = "99999";
                }
            },
        }).then(() => {
            console.warn("↩️ [401] Redirecting to login...");
            this.isHandling401 = false;

            // ✅ Redirect to login (hard reload — clears all state)
            window.location.href = "/authentication/sign-in/";
        });
    }

    async request(endpoint, options = {}) {
        const token = this.getToken();

        if (!token) {
            this.handleUnauthorized();
            throw new Error("Authentication token not found.");
        }

        const config = {
            ...options,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...options.headers,
            },
        };

        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, config);

            // ✅ Handle 401 globally
            if (response.status === 401) {
                this.handleUnauthorized();
                throw new Error("Session expired. Please log in again.");
            }

            return response;
        } catch (error) {
            console.error("API Request failed:", error);
            throw error;
        }
    }

    // ═══════════════════════════════════════
    // 🔧 Convenience methods
    // ═══════════════════════════════════════

    async get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: "GET" });
    }

    async post(endpoint, body, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: "POST",
            body: JSON.stringify(body),
        });
    }

    async put(endpoint, body, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: "PUT",
            body: JSON.stringify(body),
        });
    }

    async delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: "DELETE" });
    }
}

// ✅ Export a single instance (singleton pattern)
const apiClient = new ApiClient();
export default apiClient;